import { useState, useRef, useEffect } from 'react';
import Portal from '../atoms/Portal';
import { Calendar, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, X } from 'lucide-react';

export default function DateRangePicker({
  label,
  startDate,
  endDate,
  onChange,
  presets = [],
  activePreset = null,
  onPresetSelect,
  placeholder = 'Select date range...',
  className = '',
  clearable = true,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const now = new Date();
  const parseStartYear = startDate ? Number(startDate.slice(0, 4)) : now.getFullYear();
  const parseStartMonth = startDate ? Number(startDate.slice(5, 7)) - 1 : now.getMonth();
  const [currentYear, setCurrentYear] = useState(parseStartYear);
  const [currentMonth, setCurrentMonth] = useState(parseStartMonth);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayIndex = new Date(currentYear, currentMonth, 1).getDay();

  const toggleCalendar = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
      const refDate = startDate ? new Date(startDate) : new Date();
      setCurrentYear(refDate.getFullYear());
      setCurrentMonth(refDate.getMonth());
    }
    setIsOpen(!isOpen);
  };

  const handleSelectDay = (day) => {
    const formattedMonth = String(currentMonth + 1).padStart(2, '0');
    const formattedDay = String(day).padStart(2, '0');
    const dateStr = `${currentYear}-${formattedMonth}-${formattedDay}`;

    if (!startDate || (startDate && endDate)) {
      onChange({ startDate: dateStr, endDate: '' });
    } else if (startDate && !endDate) {
      if (new Date(dateStr) >= new Date(startDate)) {
        onChange({ startDate, endDate: dateStr });
        setIsOpen(false);
      } else {
        onChange({ startDate: dateStr, endDate: '' });
      }
    }
  };

  const handlePrevYear = () => setCurrentYear((y) => y - 1);
  const handleNextYear = () => setCurrentYear((y) => y + 1);

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const activePresetLabel = presets.find((p) => p.id === activePreset)?.label;

  const displayValue = activePresetLabel
    ? activePresetLabel
    : startDate && endDate
    ? `${startDate} ~ ${endDate}`
    : startDate
    ? `${startDate} ~ Select End`
    : '';

  useEffect(() => {
    const handleClickOutside = (e) => {
      const isOutsideTrigger = triggerRef.current && !triggerRef.current.contains(e.target);
      const isOutsidePopover = popoverRef.current && !popoverRef.current.contains(e.target);

      if (isOutsideTrigger && isOutsidePopover) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className={`form-group ${className}`} ref={triggerRef}>
      {label && <label>{label}</label>}
      <div className={`input-wrapper input-wrapper--has-prefix${(clearable && (startDate || endDate)) ? ' input-wrapper--has-suffix' : ''}`}>
        <span className="input-icon-prefix">
          <Calendar size={16} />
        </span>
        <input
          type="text"
          readOnly
          className="input-field custom-datepicker-input"
          placeholder={placeholder}
          value={displayValue}
          onClick={toggleCalendar}
        />
        {clearable && (startDate || endDate) && (
          <button
            type="button"
            className="input-icon-suffix datepicker-clear-btn"
            onClick={(e) => {
              e.stopPropagation();
              onChange({ startDate: '', endDate: '' });
            }}
            title="Hapus filter tanggal"
            aria-label="Hapus filter tanggal"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {isOpen && (
        <Portal>
          <div
            ref={popoverRef}
            className="portal-popover custom-datepicker"
            style={{
              top: coords.top,
              left: coords.left,
            }}
          >
          <div className="custom-datepicker__header">
               <button
                 className="custom-datepicker__nav-btn"
                 onClick={handlePrevYear}
                 aria-label="Previous Year"
               >
                 <ChevronsLeft size={14} />
               </button>
               <button
                 className="custom-datepicker__nav-btn"
                 onClick={handlePrevMonth}
                 aria-label="Previous Month"
               >
                 <ChevronLeft size={16} />
               </button>
               <div className="custom-datepicker__header-title">
                 {monthNames[currentMonth]} {currentYear}
               </div>
               <button
                 className="custom-datepicker__nav-btn"
                 onClick={handleNextMonth}
                 aria-label="Next Month"
               >
                 <ChevronRight size={16} />
               </button>
               <button
                 className="custom-datepicker__nav-btn"
                 onClick={handleNextYear}
                 aria-label="Next Year"
               >
                 <ChevronsRight size={14} />
               </button>
             </div>

            <div className="custom-datepicker__weekdays">
              <span>Su</span><span>Mo</span><span>Tu</span><span>We</span><span>Th</span><span>Fr</span><span>Sa</span>
            </div>

            <div className="custom-datepicker__days">
              {Array.from({ length: firstDayIndex }).map((_, i) => (
                <div key={`empty-${i}`} className="custom-datepicker__day custom-datepicker__day--empty" />
              ))}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const formattedMonth = String(currentMonth + 1).padStart(2, '0');
                const formattedDay = String(day).padStart(2, '0');
                const dateStr = `${currentYear}-${formattedMonth}-${formattedDay}`;

                const isStart = startDate === dateStr;
                const isEnd = endDate === dateStr;
                const inRange = startDate && endDate && new Date(dateStr) > new Date(startDate) && new Date(dateStr) < new Date(endDate);

                const classes = [
                  'custom-datepicker__day',
                  (isStart || isEnd) ? 'custom-datepicker__day--selected' : '',
                  inRange ? 'custom-datepicker__day--in-range' : '',
                  isStart ? 'custom-datepicker__day--range-start' : '',
                  isEnd ? 'custom-datepicker__day--range-end' : '',
                ].filter(Boolean).join(' ');

                return (
                  <div key={day} className={classes} onClick={() => handleSelectDay(day)}>
                    {day}
                  </div>
                );
              })}
            </div>

            {presets.length > 0 && (
              <div className="custom-datepicker__presets">
                {presets.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    className={`custom-datepicker__preset ${
                      activePreset === preset.id ? 'custom-datepicker__preset--active' : ''
                    }`}
                    onClick={() => {
                      if (onPresetSelect) {
                        onPresetSelect(preset.id);
                      }
                      setIsOpen(false);
                    }}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </Portal>
      )}
    </div>
  );
}
